import { useEffect, useState } from 'react';
import { useSearchParams, useParams, useLocation } from 'react-router-dom';
import { ENDPOINTS } from '../../config/constants';
import { trackPurchase } from '../../utils/metaPixel';

// Removed SendConfirmationEmail component as it's now handled by backend

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { order_id: routeOrderId } = useParams();
  const location = useLocation();
  type OrderStatus = 'verifying' | 'processing' | 'success' | 'error';
  const [status, setStatus] = useState<OrderStatus>('verifying');
  const [error, setError] = useState<string>();
  const [orderNumber, setOrderNumber] = useState<string>();
  const [processingStep, setProcessingStep] = useState<string>();

  // Helper functions for step status
  const getStepStatus = (currentStep: OrderStatus, step: 'verify' | 'process') => {
    const steps: Record<'verify' | 'process', number> = {
      verify: 1,
      process: 2
    };

    const statusOrder: Record<OrderStatus, number> = {
      verifying: 1,
      processing: 2,
      success: 3,
      error: 0
    };

    const currentStepNumber = statusOrder[currentStep];
    const targetStepNumber = steps[step];

    if (currentStepNumber === targetStepNumber) return 'current';
    if (currentStepNumber > targetStepNumber) return 'completed';
    return 'pending';
  };

  const getStepClasses = (stepStatus: 'current' | 'completed' | 'pending') => {
    switch (stepStatus) {
      case 'current':
        return 'text-white';
      case 'completed':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStepIcon = (stepStatus: 'current' | 'completed' | 'pending') => {
    switch (stepStatus) {
      case 'completed':
        return (
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'current':
        return (
          <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        );
      default:
        return <span className="w-4 h-4 mr-2" />;
    }
  };

  useEffect(() => {
    // Get order ID from URL path first, then query params
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    const pathOrderId = lastPart !== 'payment-success' ? lastPart : null;
    const queryOrderId = searchParams.get('order_id');
    const orderId = pathOrderId || queryOrderId || routeOrderId;
    
    console.log('Payment success page loaded with:', {
      pathOrderId,
      queryOrderId,
      routeOrderId,
      finalOrderId: orderId,
      pathname: location.pathname,
      search: location.search
    });
    
    if (!orderId) {
      console.error('No order_id found in URL');
      setStatus('error');
      setError('ID comandă lipsă');
      return;
    }

    // Set order number immediately to avoid flashing error
    setOrderNumber(orderId);

    let attempts = 0;
    const maxAttempts = 12; // Try for 30 seconds (2.5s * 12 = 30s)
    const interval = 2500; // Check every 2.5 seconds
    let pollTimer: number;

    const verifyPayment = async () => {
      try {
        const response = await fetch(ENDPOINTS.VERIFY_PAYMENT(orderId));
        
        // Handle gateway timeout specifically
        if (response.status === 504) {
          setStatus('processing');
          setProcessingStep('Se procesează plata, vă rugăm așteptați...');
          return false;
        }

        const data = await response.json();

        if (response.ok) {
          const responseData = data.date || data;

          // Handle payment status from response
          const status = responseData.date?.status || responseData.status;
          const message = responseData.date?.message || responseData.message;

          switch (status) {
            case 'COMPLETED':
              setStatus('success');
              // Track purchase event
              trackPurchase({
                value: 60,
                orderId: orderId
              });
              return true;

            case 'PENDING':
              setStatus('processing');
              setProcessingStep(message || 'Se procesează plata...');
              return false;

            case 'FAILED':
              setStatus('error');
              setError(message || 'Plata nu a putut fi procesată. Vă rugăm să încercați din nou.');
              return true;

            default:
              setStatus('error');
              setError(message || 'Eroare la procesarea plății. Vă rugăm să ne contactați.');
              return true;
          }
        }

        // If payment is still processing, continue polling
        if (response.status === 400 && data.mesaj === 'Plata nu a fost finalizată') {
          return false;
        }

        // For other errors, stop polling and show error
        throw new Error(data.mesaj || 'Eroare la verificarea plății');
      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('error');
        setError(error instanceof Error ? error.message : 'Eroare la verificarea plății');
        return true; // Stop polling on error
      }
    };

    const poll = async () => {
      attempts++;
      const shouldStop = await verifyPayment();
      
      if (shouldStop || attempts >= maxAttempts) {
        if (attempts >= maxAttempts && (status === 'verifying' || status === 'processing')) {
          setStatus('error');
          setError('Plata a fost înregistrată cu succes. Veți primi în curând un email cu, confirmarea comenzii, factura și AWB-ul pentru livrare.');
        }
        return;
      }

      pollTimer = window.setTimeout(poll, interval);
    };

    poll();

    return () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
      }
    };
  }, [searchParams, routeOrderId, status, location]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-gradient-to-b from-slate-900 to-black rounded-xl shadow-2xl border border-red-500/20 p-8">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 via-purple-500 to-red-500 rounded-t-xl" />

      {(status === 'verifying' || status === 'processing') && (
        <div className="text-center">
          <div className="mb-8">
            <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full mx-auto animate-spin" />
          </div>
          <h2 className="text-2xl font-bold mb-4">
            {processingStep || 'Se procesează comanda...'}
          </h2>
          <p className="text-gray-400 mb-4">
            {status === 'verifying' && 'Se verifică plata dumneavoastră'}
            {status === 'processing' && 'Se procesează comanda dumneavoastră'}
          </p>
          <div className="mt-6 space-y-2 text-sm text-gray-400">
            <p>Acest proces poate dura până la 30 de secunde.</p>
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="font-medium">Pașii procesării:</p>
                <ul className="list-none space-y-2">
                  {/* Verification Step */}
                  <li className={`flex items-center ${getStepClasses(getStepStatus(status, 'verify'))}`}>
                    {getStepIcon(getStepStatus(status, 'verify'))}
                    Verificare plată
                  </li>

                  {/* Processing Step */}
                  <li className={`flex items-center ${getStepClasses(getStepStatus(status, 'process'))}`}>
                    {getStepIcon(getStepStatus(status, 'process'))}
                    Procesare comandă
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-6 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-4">Plată procesată cu succes!</h2>
              {orderNumber && (
                <p className="text-gray-400 mb-4">Număr comandă: {orderNumber}</p>
              )}
              <div className="text-gray-400 space-y-4">
                <p>Vă mulțumim pentru comandă!</p>
              <p>Veți primi în următoarele minute un email cu confirmarea comenzii, factura și AWB-ul pentru livrare.</p>
              </div>
              <a
                href="/"
                className="mt-6 inline-block bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2 rounded-md hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Înapoi la pagina principală
              </a>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500 rounded-full mx-auto mb-6 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-4">Eroare la procesarea plății</h2>
              {error && (
                <p className="text-gray-400 mb-4">{error}</p>
              )}
              <div className="text-gray-400">
                <p>Vă rugăm să încercați din nou sau să ne contactați pentru asistență.</p>
              </div>
              <a
                href="/"
                className="mt-6 inline-block bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2 rounded-md hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Înapoi la pagina principală
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
